import { EventBus } from '@antification/core';

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'info' | 'achievement';
  timestamp: number;
  duration: number;
}

export class NotificationManager {
  private events: EventBus;
  private toasts: Toast[] = [];
  private nextId = 0;
  private readonly MAX_VISIBLE = 4;
  private readonly DEFAULT_DURATION = 4000;

  // Corporate messages pool
  private static readonly SUCCESS_PREFIXES = [
    '🌟', '✨', '💫', '⭐', '🎯', '✅', '👏', '🏗️',
  ];

  private static readonly CORPORATE_SUFFIXES = [
    'Excellent work!',
    'You\'re making great progress!',
    'Stakeholders are pleased!',
    'Development potential: MAXIMIZED',
    'Efficiency metrics updated!',
    'Community impact: POSITIVE',
    'Another step toward accessibility!',
    'Your contribution matters!',
  ];

  constructor(events: EventBus) {
    this.events = events;

    // Listen for notification events
    this.events.on('NOTIFICATION_SHOW', (payload) => {
      const data = payload as { message: string; type?: Toast['type'] };
      this.show(data.message, data.type || 'success');
    });

    this.events.on('ACHIEVEMENT_UNLOCKED', (payload) => {
      const data = payload as { name: string; description: string };
      this.showAchievement(data.name, data.description);
    });
  }

  show(message: string, type: Toast['type'] = 'success'): void {
    const toast: Toast = {
      id: this.nextId++,
      message: this.corporatize(message, type),
      type,
      timestamp: performance.now(),
      duration: this.DEFAULT_DURATION,
    };

    this.toasts.push(toast);

    // Limit visible toasts
    while (this.toasts.length > this.MAX_VISIBLE) {
      this.toasts.shift();
    }
  }

  showAchievement(name: string, description: string): void {
    const toast: Toast = {
      id: this.nextId++,
      message: `🏆 Achievement Unlocked: ${name}\n${description}`,
      type: 'achievement',
      timestamp: performance.now(),
      duration: 6000, // Achievements stay longer
    };

    this.toasts.push(toast);
  }

  private corporatize(message: string, type: Toast['type']): string {
    if (type === 'achievement') return message;

    // Add random emoji prefix
    const prefix = NotificationManager.SUCCESS_PREFIXES[
      Math.floor(Math.random() * NotificationManager.SUCCESS_PREFIXES.length)
    ];

    // Sometimes add corporate suffix
    if (Math.random() > 0.7) {
      const suffix = NotificationManager.CORPORATE_SUFFIXES[
        Math.floor(Math.random() * NotificationManager.CORPORATE_SUFFIXES.length)
      ];
      return `${prefix} ${message} ${suffix}`;
    }

    return `${prefix} ${message}`;
  }

  update(): void {
    const now = performance.now();

    // Remove expired toasts
    this.toasts = this.toasts.filter(
      (toast) => now - toast.timestamp < toast.duration
    );
  }

  render(ctx: CanvasRenderingContext2D, screenWidth: number): void {
    const now = performance.now();
    const toastWidth = 350;
    const toastHeight = 50;
    const padding = 15;
    const startY = 70;

    this.toasts.forEach((toast, index) => {
      const age = now - toast.timestamp;
      const progress = age / toast.duration;

      // Fade in/out
      let alpha = 1;
      if (progress < 0.1) {
        alpha = progress / 0.1;
      } else if (progress > 0.8) {
        alpha = 1 - (progress - 0.8) / 0.2;
      }

      // Slide in from right
      const slideProgress = Math.min(1, age / 200);
      const xOffset = (1 - this.easeOutQuad(slideProgress)) * 100;

      const x = screenWidth - toastWidth - 20 + xOffset;
      const y = startY + index * (toastHeight + 10);

      // Background
      ctx.save();
      ctx.globalAlpha = alpha;

      // Different colors for different types
      let bgColor: string;
      let borderColor: string;
      let textColor: string;

      switch (toast.type) {
        case 'achievement':
          bgColor = 'rgba(255, 215, 0, 0.95)';
          borderColor = '#b8860b';
          textColor = '#333';
          break;
        case 'info':
          bgColor = 'rgba(255, 255, 255, 0.95)';
          borderColor = '#2d5a7b';
          textColor = '#333';
          break;
        default:
          bgColor = 'rgba(232, 244, 244, 0.95)';
          borderColor = '#4caf50';
          textColor = '#333';
      }

      ctx.fillStyle = bgColor;
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 2;

      ctx.beginPath();
      ctx.roundRect(x, y, toastWidth, toastHeight, 8);
      ctx.fill();
      ctx.stroke();

      // Text
      ctx.fillStyle = textColor;
      ctx.font = '13px "Segoe UI", system-ui, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';

      // Handle multiline for achievements
      const lines = toast.message.split('\n');
      if (lines.length > 1) {
        ctx.font = 'bold 12px "Segoe UI", system-ui, sans-serif';
        ctx.fillText(lines[0], x + padding, y + toastHeight / 2 - 8);
        ctx.font = '11px "Segoe UI", system-ui, sans-serif';
        ctx.fillText(lines[1], x + padding, y + toastHeight / 2 + 10);
      } else {
        ctx.fillText(toast.message, x + padding, y + toastHeight / 2);
      }

      ctx.restore();
    });
  }

  private easeOutQuad(t: number): number {
    return t * (2 - t);
  }
}

// Loading screen tips for that corporate feel
export const LOADING_TIPS = [
  "Did you know? 43% of urban America is parking lots. YOU'RE PART OF SOMETHING BIGGER.",
  "Convenient parking increases local business revenue by up to 23%!",
  "Every parking spot brings communities together.",
  "Optimizing vehicle throughput since 2024.",
  "Your development potential: EXCELLENT",
  "Sustainability Mode: Now with 15% more green icons!",
  "Remember: A well-organized lot is a happy lot.",
  "Fun fact: The average car spends 95% of its life parked.",
  "Building accessible communities, one spot at a time.",
  "Chester the Cement Mixer says: 'Have a concrete day!'",
  "Pro tip: Straight lines maximize stakeholder satisfaction.",
  "Environmental Impact: ✅ Assessed",
  "Your parking lot could feature in our annual report!",
  "Efficiency isn't just a metric. It's a way of life.",
  "Remember: 'Surface preparation' sounds better than 'deforestation'.",
  "Trees removed are trees relocated to a better place.",
  "Quality asphalt: Because communities deserve the best.",
  "Parking: Where vehicles rest and dreams come true.",
  "You're not just building a lot. You're building futures.",
  "Carbon Neutral*: *See appendix for calculation methodology.",
];
